import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useModalState, type UseModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CONTROL_TYPES, type ControlType } from "../types";
import { hasSpendingLimitIncreaseRequest } from "../utils/spendingLimitRequest";

/**
 * Over-limit details snapshotted at open time, because opening the dialog closes the editor — which
 * would otherwise collapse the live overage before the dialog can read it.
 */
export interface BoostIncreaseRequestContext {
  overageInMinorUnits: number;
  currentLimitInDollars?: number;
  qualification?: string;
}

interface UseBoostLimitIncreaseRequestParams {
  workplaceId: string;
  weekStartDate: Date | undefined;
  /** Last-updated timestamp of the variableCharge control; a request is pending only if made after it. */
  controlUpdatedAt: Date | undefined;
  /**
   * Editor's modal state — closed when the request opens, reopened on Back (one dialog at a time).
   * Optional: surfaces with no separate editor dialog (inline forms, or a request dialog that overlays
   * the caller) omit it, and the request modal opens standalone.
   */
  editModalState?: UseModalState;
}

export interface UseBoostLimitIncreaseRequestResult {
  modalState: UseModalState;
  controlType: ControlType;
  isRequested: boolean;
  context: BoostIncreaseRequestContext | undefined;
  open: (context: BoostIncreaseRequestContext) => void;
  back: () => void;
  markRequested: () => void;
  reset: () => void;
}

/**
 * Shared wiring for the boost (variableCharge) increase-request flow: owns the dialog state, the
 * "Requested" detection (session flag or persisted request), and the over-limit snapshot.
 */
export function useBoostLimitIncreaseRequest(
  params: UseBoostLimitIncreaseRequestParams
): UseBoostLimitIncreaseRequestResult {
  const { workplaceId, weekStartDate, controlUpdatedAt, editModalState } = params;

  const modalState = useModalState();
  // Flips the card's button to "Increase requested" immediately after submit; persisted check covers reopen.
  const [requestedThisSession, setRequestedThisSession] = useState(false);
  const [context, setContext] = useState<BoostIncreaseRequestContext | undefined>(undefined);

  // Reset the session flag when the target week changes (e.g. switching shifts without closing the
  // editor), so a prior submit doesn't leak "Requested" onto a different week. Keyed on the timestamp
  // because callers may pass a fresh Date instance each render.
  const weekStartTime = weekStartDate?.getTime();
  useEffect(() => {
    setRequestedThisSession(false);
  }, [weekStartTime]);

  const isRequested = useMemo(
    () =>
      requestedThisSession ||
      (isDefined(weekStartDate) &&
        hasSpendingLimitIncreaseRequest(
          workplaceId,
          weekStartDate,
          controlUpdatedAt,
          CONTROL_TYPES.variableCharge
        )),
    [requestedThisSession, workplaceId, weekStartDate, controlUpdatedAt]
  );

  // Close the editor when the request opens so only one dialog is visible at a time.
  const open = useCallback(
    (nextContext: BoostIncreaseRequestContext) => {
      // Log on click, not on the dialog's mount, so intent is captured even if the dialog fails to render.
      logEvent(APP_V2_USER_EVENTS.SPENDING_CONTROL_INCREASE_REQUEST_CLICKED, {
        workplaceId,
        weekStartDate: weekStartDate?.toISOString(),
        controlType: CONTROL_TYPES.variableCharge,
        overageInMinorUnits: nextContext.overageInMinorUnits,
        currentLimitInDollars: nextContext.currentLimitInDollars,
        qualification: nextContext.qualification,
      });
      setContext(nextContext);
      modalState.openModal();
      editModalState?.closeModal();
    },
    [modalState, editModalState, workplaceId, weekStartDate]
  );

  const back = useCallback(() => {
    modalState.closeModal();
    editModalState?.openModal();
  }, [modalState, editModalState]);

  const markRequested = useCallback(() => {
    setRequestedThisSession(true);
  }, []);

  const reset = useCallback(() => {
    modalState.closeModal();
    setRequestedThisSession(false);
  }, [modalState]);

  // Not memoized: `useModalState` returns a fresh object each render, so a memo wouldn't add stability.
  // Consumers that list `increaseRequest` in deps key off its stable sub-fields (`modalState.modalIsOpen`,
  // `isRequested`).
  return {
    modalState,
    controlType: CONTROL_TYPES.variableCharge,
    isRequested,
    context,
    open,
    back,
    markRequested,
    reset,
  };
}
